/**
 * How a status looks in the library: a Chip tone and a Light state (src/ui, admin-design-r1). Pure, and typed as the
 * library's own words so a page passes them straight through. Replaces the old StatusChip tone table, which mixed run,
 * step, founder and presence words in one place.
 *
 * Signal (`down`) only for down: a failed run or step, an offline box. Waiting on a person (manual, behind) is ink.
 */
import type { LightState as ApiLightState, Light as ApiLight } from './schemas'

export type ChipTone = 'plain' | 'work' | 'look' | 'down' | 'mute'
export type LightLook = 'ok' | 'work' | 'look' | 'down' | 'off'

export interface StatusLook {
  tone: ChipTone
  light: LightLook
}

const OK: StatusLook = { tone: 'plain', light: 'ok' }
const WORK: StatusLook = { tone: 'work', light: 'work' }
const DOWN: StatusLook = { tone: 'down', light: 'down' }
const LOOK: StatusLook = { tone: 'look', light: 'look' }
const IDLE: StatusLook = { tone: 'plain', light: 'off' }
const MUTE: StatusLook = { tone: 'mute', light: 'off' }

const LOOKS: Record<string, StatusLook> = {
  done: OK, active: OK, online: OK,
  running: WORK, provisioning: WORK, creating: WORK, new: WORK,
  failed: DOWN, offline: DOWN,
  manual: LOOK, behind: LOOK,
  pending: IDLE, invited: IDLE, unknown: IDLE,
  skipped: MUTE, left: MUTE, paused: MUTE, destroyed: MUTE, removed: MUTE,
}

/** A run, step, founder, box or presence status as a chip. A status this app doesn't know reads as idle. */
export function statusLook(status: string): StatusLook {
  return LOOKS[status] ?? IDLE
}

/** The API's four light states in the library's five: a warning needs a look, unknown is off. */
export function lightLook(state: ApiLightState): LightLook {
  return ({ ok: 'ok', warn: 'look', down: 'down', unknown: 'off' } as const)[state]
}

/** The API's lights as the library's LightStrip takes them. */
export function lightSignals(lights: ApiLight[]): { key: string; label: string; state: LightLook; detail: string }[] {
  return lights.map((l) => ({ key: l.key, label: l.label.toLowerCase(), state: lightLook(l.state), detail: l.detail }))
}

/** A finding's severity as a light: down is Signal, a warning needs a look, a note is off. */
export function severityLook(severity: 'down' | 'warn' | 'info'): LightLook {
  return severity === 'down' ? 'down' : severity === 'warn' ? 'look' : 'off'
}
